import ContextTriggerWebComponent from "./components/web-components/context-trigger";
import DebugDashboardWebComponent from "./components/web-components/debug-dashboard";
import DrawerWebComponent from "./components/web-components/drawer";
import LandingSearchBoxWebComponent from "./components/web-components/landing-search-box";
import PlaygroundWebComponent from "./components/web-components/playground";
import SearchBoxWebComponent from "./components/web-components/search-box";
import { Sentry } from "./instrument";
import { useDrawerState } from "./store/drawer-state";
import { env } from "./env";

import "@qa-app/core/widget-font-assets";

/**
 * Resolve the API base URL from the first pollen-* element on the page that
 * carries an `api-url` attribute. Falls back to the build-time VITE_API_URL
 * (intended for local dev only — production tenants must set `api-url`
 * explicitly on each element).
 */
const resolveApiUrl = (): string => {
  const selector = [
    "pollen-search-box[api-url]",
    "pollen-landing-search-box[api-url]",
    "pollen-context-trigger[api-url]",
    "pollen-drawer[api-url]",
  ].join(", ");

  const element = document.querySelector<HTMLElement>(selector);
  const apiUrl = element?.getAttribute("api-url")?.trim();
  return apiUrl || env.API_URL;
};

// Function to safely register custom elements (avoid "already used" errors)
const safeDefineCustomElement = (
  name: string,
  constructor: CustomElementConstructor,
) => {
  if (!customElements.get(name)) {
    customElements.define(name, constructor);
  }
};

// Set up search button click handler — host pages that include a legacy
// `#qna-search-btn` button get the drawer-open behavior for free. Pages
// without this element are unaffected.
const setupSearchButtonHandler = () => {
  const searchBtn = document.querySelector("#qna-search-btn");
  if (searchBtn) {
    const newSearchBtn = searchBtn.cloneNode(true);
    searchBtn.parentNode?.replaceChild(newSearchBtn, searchBtn);

    newSearchBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      console.info("Search button clicked - opening drawer and hiding modal");

      const searchContentModal = document.querySelector("#SearchContent");
      if (searchContentModal) {
        (searchContentModal as HTMLElement).style.display = "none";
        searchContentModal.setAttribute("aria-hidden", "true");
      }

      const modalBackdrop = document.querySelector(".modal-backdrop.show");
      if (modalBackdrop) {
        (modalBackdrop as HTMLElement).style.display = "none";
      }

      const { openDrawer } = useDrawerState.getState();
      openDrawer();
    });
  }
};

// Always register the debug component first (for debugging even if API is down).
//
// Names are namespaced with `pollen-drawer-*` to avoid colliding with the
// equivalent elements in `tenant-ai-widget` (which registers
// `pollen-debug-dashboard` and `pollen-playground` directly). Both bundles
// coexist on the admin playground page; the first `customElements.define()`
// call for a given tag wins for the page's lifetime, so sharing names would
// cause the second bundle to silently fall back to whichever implementation
// loaded first — including divergent behavior such as `openDrawer()` vs
// `openModal()`.
safeDefineCustomElement("pollen-drawer-debug-dashboard", DebugDashboardWebComponent);
safeDefineCustomElement("pollen-drawer-playground", PlaygroundWebComponent);

const API_URL = resolveApiUrl();

if (!API_URL) {
  console.warn(
    "Pollen Drawer Widget: no API URL resolved. Provide an `api-url` attribute on a pollen-* element or set VITE_API_URL at build time.",
  );
}
else {
  fetch(`${API_URL}/health`)
    .then(res => res.json())
    .then((data) => {
      if (data.status === "ok") {
        safeDefineCustomElement("pollen-search-box", SearchBoxWebComponent);
        safeDefineCustomElement("pollen-drawer", DrawerWebComponent);
        safeDefineCustomElement("pollen-context-trigger", ContextTriggerWebComponent);
        safeDefineCustomElement(
          "pollen-landing-search-box",
          LandingSearchBoxWebComponent,
        );

        setupSearchButtonHandler();
        setTimeout(setupSearchButtonHandler, 100);
      }
      return data;
    })
    .catch((error) => {
      Sentry.captureException(error, {
        tags: {
          component: "widget-initialization",
          context: "health-check",
        },
        extra: {
          apiUrl: API_URL,
        },
      });
      console.error("Health check failed:", error);
    });
}
